import { ColumnsFilter, UserProfile } from "../../common/services/types";

export type LinkType = "Relation" | "Duplicate" | "Block" | "Priority";
export type ActionStatus =
    | "Started"
    | "Completed"
    | "Failed"
    | "AttentionRequired";

export type PrincipalType = "User" | "ApiKey" | "System" | "Unknown";

export interface PrincipalInfo {
    type: PrincipalType;
    id: string;
    displayName: string;
    source?: string | null;
    isInactive: boolean;
    hasAvatar: boolean;
}

export type TaskUpdateFieldName =
    | "AssignedTo"
    | "Deleted"
    | "Description"
    | "DueDate"
    | "Name"
    | "Priority"
    | "Reminder"
    | "Status"
    | "TaskType";

export class TaskBase {
    id: string;
    name: string;
    createDate: string;
    updateDate: string;
    createdBy: string;
    createdByActor?: PrincipalInfo | null;
    assignedTo: string;
    priority: string;
    projectId: string;
    status: string;
    dueDate: string;
    taskType: string;
    alphaId: AlphaId;
    taskId: string;
    isDone: boolean;
    metadata?: TaskMetadata;
}

export class Task extends TaskBase {
    followers: string[];
    statusName: string;
    subTaskIds: string[];
    attachmentsCount: number;
}

export interface SubtaskMeta {
    subtaskId: string;
    name: string;
    statusId: string;
    statusName: string;
    alphaId: AlphaId;
    assignedTo: string | null;
}

export interface ParentTaskMeta {
    taskId: string;
    alphaId: AlphaId;
    name: string;
}

export type TaskModel = Task;

export interface TaskListModel extends TaskModel {
    isSelected?: boolean;
}

export enum MetadataAuditSourceEnum {
    ManualEdit = "Manual Edit",
    AnalyseAction = "Analyse Action",
    FlowAutoSync = "Flow Auto-Sync",
}

export interface MetadataAudit {
    id?: string;
    taskId: string;
    fieldName: string;
    oldValue?: unknown | null;
    newValue?: unknown | null;
    updatedBy: string;
    updatedByActor?: PrincipalInfo | null;
    timestamp: string;
    source?: MetadataAuditSourceEnum | null;
}

export interface TaskPreviewModel {
    id: string;
    packageSetId: string;
    name: string;
    projectId: string;
    deleted: boolean;
    taskType: string;
    alphaId: string;
    status: string;
    priority: string;
    isSubtask: boolean;
    reminder: string;
    statusName: string;
    dueDate: string;
    followers: string[];
    assignedTo: string;
    sharedWith: string[] | null;
    createdBy: string;
    createdByActor?: PrincipalInfo | null;
    taskId: string;
    isManuallyCreated?: boolean | undefined;
    errors: TaskError[];
    metadataAudits: MetadataAudit[];
    description?: string;
    parentTask?: ParentTaskMeta;
    subTasksMeta?: SubtaskMeta[];
    metadata?: TaskMetadata;
}

export enum EntityTypeEnum {
    Contract = "Contract",
    Claim = "Claim",
    Worksheet = "Worksheet",
    EMessage = "EMessage",
    Unknown = "Unknown",
}

export enum MetadataTypeEnum {
    Submission = "Submission",
    Quote = "Quote",
    Renewal = "Renewal",
    Binder = "Binder",
    Endorsement = "Endorsement",
    Policy = "Policy",
    Certificate = "Certificate",
    NewClaim = "NewClaim",
    ClaimUpdate = "ClaimUpdate",
    FinancialClaimUpdate = "FinancialClaimUpdate",
    ClaimClosure = "ClaimClosure",
    Other = "Other",
}

export enum SourceSystemEnum {
    Email = "Email",
    Manual = "Manual",
    SFTP = "SFTP",
    Flow = "Flow",
}

export enum ConfidenceEnum {
    Low = "Low",
    Medium = "Medium",
    High = "High",
}

export interface CoreMetadata {
    submissionId?: string;
    entityType?: EntityTypeEnum;
    type?: MetadataTypeEnum;
    sourceSystem?: SourceSystemEnum;
    channel?: string;
    confidence?: ConfidenceEnum;
    enrichmentStatus?: string;
    enrichmentUpdatedAt?: string;
}

export interface ContractMetadata {
    businessId?: string;
    umr?: string;
    contractReference?: string;
    title?: string;
    uwYear?: number;
    participation?: string;
    mainClass?: string;
    currency?: string;
    cedent?: string;
    broker?: string;
}

export interface ClaimMetadata {
    claimId?: string;
    ucr?: string;
    claimReference?: string;
    claimName?: string;
    lossDate?: string;
    cedent?: string;
    broker?: string;
    insured?: string;
    umr?: string;
    insuredPeriod?: string;
    contractType?: string;
    currency?: string;
    amount?: number;
    isHeadlineLoss?: boolean;
}

export interface WorksheetMetadata {
    worksheetId?: string;
    worksheetType?: string;
    accountingPeriod?: string;
}

export interface EMessageMetadata {
    uuid?: string;
}

export interface DomainMetadata {
    contracts?: ContractMetadata;
    claims?: ClaimMetadata;
    worksheets?: WorksheetMetadata;
    eMessages?: EMessageMetadata;
}

export interface TaskMetadata {
    core?: CoreMetadata;
    domain?: DomainMetadata;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    project?: { [key: string]: any };
}

export interface TaskCommentModel {
    id: string;
    userId: string;
    userName: string;
    authorActor?: PrincipalInfo | null;
    createdDate: string;
    updatedDate: string;
    body: string;
    taskId: string;
}

export interface TaskActivity {
    id: string;
    taskId: string;
    activityType: string;
    doneBy: string;
    doneByActor?: PrincipalInfo | null;
    message: string;
    createdTime: string;
}

export interface TaskActivitiesResult {
    getTaskActivities: TaskActivity[];
}

export interface SortModel {
    field: string;
    order: "ascend" | "descend";
}

export interface PackageSetPreviewModel {
    id: string;
    name: string;
    packages: PackagePreviewModel[];
}

export class PackageBase {
    fileName: string;
    filePath: string;
    contentType: string;
    fileSizeBytes: number;
    uploadedTime: string;
    state: string;
    extension: string;
}

export class PackagePreviewModel extends PackageBase {
    id: string;
    indexDate: string;
}

export interface AttachmentUpdateModel {
    packageSetId: string;
    attachmentId: string | undefined;
    action: "add" | "remove";
}

export interface FollowersChangeModel {
    taskId: string;
    followers: string[];
}

export interface AssignedToChangesModel {
    taskId: string;
    assignedTo: string;
}

export interface EmailMetadata {
    from: string | undefined;
    to: string[] | undefined;
    body: string | undefined;
    subject: string | undefined;
    date: string | undefined;
}

export interface UserModel {
    userName: string;
    id: string;
}

export interface TasksData {
    tasks: TaskModel[];
    tasksCount: number;
    userColumnsFilter: {
        filters: ColumnsFilter[];
    } | null;
    widgetFilters: ColumnsFilter[];
    widgetName?: string;
}

export interface TasksDataResult {
    getTasksData: TasksData;
}

export interface PackageSetPackagesResult {
    getPackagesFromSet: PackagePreviewModel[];
}

export interface TaskCommentsResult {
    getTaskComments: TaskCommentModel[];
}

export interface TaskPrefix {
    id: string;
    name: string;
    projectId: string;
    counter: number;
}

export interface TaskPrefixForProjectResult {
    getTaskPrefixByProjectId: TaskPrefix | null;
}

export interface GetUserProfileByIdResult {
    getUserProfileById: UserProfile;
}
export interface ColumnModel {
    name: string;
    isVisible: boolean;
    isMetaData: boolean;
    width: number;
}

export class ColumnModelBase {
    isVisible: boolean;
    isMetaData: boolean;
    width: number;
}

export class MetadataColumn extends ColumnModelBase {
    metaDataId: string;
}

export class DefaultColumn extends ColumnModelBase {
    name: string;
}

export type TaskError = {
    id: string;
    source: string;
    taskId: string;
    description: string;
    details: ErrorDetails;
    createdAt: string;
};

export type MetadataPushMessage = {
    taskId: string;
    fields: { [key: string]: unknown };
    audits: MetadataAudit[];
};

export type ErrorDetails = {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    [key: string]: any[];
};

export type QueryRequestData = {
    name: string;
    details: string;
    email: string;
    phone: string;
    phoneType: string;
    fileData: { packageId: string; name: string; contentType: string }[];
};

export interface TaskLookupItem {
    id: string;
    name: string;
    alphaId?: AlphaId;
    isSubtask: boolean;
}

export interface AlphaId {
    prefix: string;
    index: number;
}

export interface TaskLink {
    id: string;
    taskId: string;
    taskIdentifier?: AlphaId;
    taskTitle: string;
    linkType: LinkType;
    linkedTaskId: string;
    linkedTaskIdentifier?: AlphaId;
    linkedTaskTitle: string;
    comments?: string;
    isSubtask: boolean;
}

export type TaskRelashionshipOption = {
    value: string;
    label: string;
    type: LinkType;
    directional: boolean;
    inverse?: boolean;
};

export type SubtaskFromTemplates = {
    name?: string;
    description?: string;
};

export class TaskRelationships {
    static options: TaskRelashionshipOption[] = [
        {
            value: "relation",
            label: "Relates to",
            type: "Relation",
            directional: false,
        },
        {
            value: "duplicate",
            label: "Duplicates",
            type: "Duplicate",
            directional: false,
        },
        {
            value: "block-1",
            label: "Blocks",
            type: "Block",
            directional: true,
            inverse: false,
        },
        {
            value: "block-2",
            label: "Blocked by",
            type: "Block",
            directional: true,
            inverse: true,
        },
        {
            value: "priority-1",
            label: "Precedes",
            type: "Priority",
            directional: true,
            inverse: false,
        },
        {
            value: "priority-2",
            label: "Follows",
            type: "Priority",
            directional: true,
            inverse: true,
        },
    ];

    static getRelationInfo(link: TaskLink, currentTaskId: string) {
        return this.options.find(
            (x) =>
                x.type === link.linkType &&
                (!x.directional ||
                    x.inverse === (currentTaskId !== link.taskId)),
        );
    }

    static getOption(value: string) {
        return this.options.find((x) => x.value === value);
    }
}

export type StepHistory = {
    status: StepHistoryStatus;
    startTimestamp: string;
    endTimestamp?: string;
    details?: string;
    duration?: string;
    errorMessage?: string;
};

export enum StepHistoryStatus {
    Starting = "Starting",
    Uploading = "Uploading",
    Preparing = "Preparing",
    MonitoringUploadStatus = "Monitoring document upload status",
    UploadCompleted = "Upload completed with status: Ready",
    Analyzing = "Analyzing",
    Attention = "Attention",
    Failed = "Failed",
}

export interface ActionEvent {
    id: string;
    userId: string;
    triggeredByActor?: PrincipalInfo | null;
    actionDefinitionId: string;
    actionType: string;
    taskId: string;
    packageId?: string;
    status: ActionStatus;
    resultUrl?: string;
    message?: string;
    result?: unknown;
    triggeredOn: string;
    completedOn?: string;
    fields: unknown;
    stepHistory: StepHistory[];
}

export interface ActionCompletedResult {
    message: string;
    event: ActionEvent;
}

export interface ActionTriggeredResult {
    event: ActionEvent;
}
